import { memo, useEffect, useCallback, useRef } from "react";
import { useAtom, useSetAtom, useAtomValue } from 'jotai';
import { message } from 'antd';
import { currentProjectAtom } from "~/stores/ProjectStore";
import { isMapLoadedDoneAtom } from "~/components/Map/stores/mapStore";
import {
  projectMembersLocationAtom,
  memberDisplayedBoundsAtom,
  showMemberLabelsAtom,
  projectMembersMapAtom,
  ZOOM_TO_MEMBER_EVENT,
  ZoomToMemberEventDetail,
  parseMemberLocationCoords,
} from "./MemberStore";
import { userProfileModalAtom } from "~/components/Map/components/UserProfile/UserProfileStore";
import { UserLocation_Base } from "~/common/typescript/models/UserLocation";
import { UserLocationAPIs } from "~/common/typescript/apis/UserLocationAPIs";
import { LngLatBounds } from "maplibre-gl";
import { MemberMapUtilOptimized } from "./MemberMapUtilOptimized";
import maplibregl from 'maplibre-gl';
import { formatDateTime } from "~/utils/dateUtils";

const MemberMapController = () => {
  const [currentProject, setCurrentProject] = useAtom(currentProjectAtom);
  const [isMapLoadedDone, setIsMapLoadedDone] = useAtom(isMapLoadedDoneAtom);
  const [projectMembersLocation, setProjectMembersLocation] = useAtom(projectMembersLocationAtom);
  const projectMembersMap = useAtomValue(projectMembersMapAtom);
  const setMemberDisplayedBounds = useSetAtom(memberDisplayedBoundsAtom);
  const memberDisplayedBounds = useAtomValue(memberDisplayedBoundsAtom);
  const showMemberLabels = useAtomValue(showMemberLabelsAtom);
  const setUserProfileModal = useSetAtom(userProfileModalAtom);
  const activePopupRef = useRef<maplibregl.Popup | null>(null);

  // Auto-refresh refs
  const autoRefreshInterval = useRef<NodeJS.Timeout | null>(null);
  const isAutoRefreshEnabled = useRef(true); // Default enabled

  const fetchData = async (isAutoRefresh = false) => {
    if (!currentProject || !currentProject.id) return;
    try {
      if (isAutoRefresh) {
        console.log('Auto-refreshing member locations...');
      }

      // Fetch only member locations (do not load users with roles on map load)
      const membersLocation = await UserLocationAPIs.remote_getUserLocationOfProject(currentProject.id);

      setProjectMembersLocation(membersLocation || []);
    } catch (error) {
      console.error("Error fetching project members location:", error);
      setProjectMembersLocation([]);
    }
  };

  // Start auto-refresh
  const startAutoRefresh = useCallback(() => {
    if (autoRefreshInterval.current) {
      clearInterval(autoRefreshInterval.current);
    }

    if (isAutoRefreshEnabled.current && currentProject?.id) {
      autoRefreshInterval.current = setInterval(() => {
        fetchData(true);
      }, 5000); // 5 seconds

      console.log('Member location auto-refresh started (5s interval)');
    }
  }, [currentProject?.id]);

  // Stop auto-refresh
  const stopAutoRefresh = useCallback(() => {
    if (autoRefreshInterval.current) {
      clearInterval(autoRefreshInterval.current);
      autoRefreshInterval.current = null;
      console.log('Member location auto-refresh stopped');
    }
  }, []);

  // Toggle auto-refresh
  const toggleAutoRefresh = useCallback((enabled: boolean) => {
    isAutoRefreshEnabled.current = enabled;
    if (enabled) {
      startAutoRefresh();
    } else {
      stopAutoRefresh();
    }
  }, [startAutoRefresh, stopAutoRefresh]);

  useEffect(() => {
    if (!currentProject || !isMapLoadedDone) return;

    fetchData();

    // Start auto-refresh when project loads
    if (isAutoRefreshEnabled.current) {
      startAutoRefresh();
    }

    // Cleanup on project change
    return () => {
      stopAutoRefresh();
    };
  }, [currentProject, isMapLoadedDone, startAutoRefresh, stopAutoRefresh]);

  // Update map when projectMembersLocation or showMemberLabels changes
  useEffect(() => {
    if (!isMapLoadedDone) return;
    MemberMapUtilOptimized.updateMembers(projectMembersLocation, showMemberLabels);
  }, [isMapLoadedDone, projectMembersLocation, showMemberLabels]);

  useEffect(() => {
    if (!isMapLoadedDone) return;

    let combinedMemberBounds: LngLatBounds | null = null;
    projectMembersLocation.forEach(memberLocation => {
      const coords = parseMemberLocationCoords(memberLocation?.location);
      if (coords) {
        const memberBounds = new LngLatBounds([coords.lng, coords.lat], [coords.lng, coords.lat]);
        if (combinedMemberBounds) {
          combinedMemberBounds.extend(memberBounds);
        } else {
          combinedMemberBounds = memberBounds;
        }
      }
    });

    if (combinedMemberBounds) {
      setMemberDisplayedBounds(JSON.stringify([
        [combinedMemberBounds.getWest(), combinedMemberBounds.getSouth()],
        [combinedMemberBounds.getEast(), combinedMemberBounds.getNorth()],
      ]));
    } else {
      setMemberDisplayedBounds(null);
    }
  }, [isMapLoadedDone, projectMembersLocation, setMemberDisplayedBounds]);

  // Function to zoom to member bounds - can be called externally
  const zoomToMemberBounds = useCallback(() => {
    if (!window.map || !memberDisplayedBoundsAtom) return;

    try {
      const boundsString = memberDisplayedBounds;
      if (boundsString) {
        const bounds = JSON.parse(boundsString);
        const lngLatBounds = new LngLatBounds(bounds[0], bounds[1]);
        window.map.fitBounds(lngLatBounds, {
          padding: 50,
          maxZoom: 15
        });
      }
    } catch (error) {
      console.error("Error zooming to member bounds:", error);
    }
  }, [memberDisplayedBounds]);

  // Make functions available globally for other components to use
  useEffect(() => {
    (window as any).zoomToMemberBounds = zoomToMemberBounds;
    (window as any).toggleMemberAutoRefresh = toggleAutoRefresh;
    (window as any).refreshMemberLocations = () => fetchData(false);

    return () => {
      delete (window as any).zoomToMemberBounds;
      delete (window as any).toggleMemberAutoRefresh;
      delete (window as any).refreshMemberLocations;
    };
  }, [zoomToMemberBounds, toggleAutoRefresh]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopAutoRefresh();
    };
  }, [stopAutoRefresh]);

  // Listen to global ZOOM_TO_MEMBER_EVENT dispatched from DashboardTab, MembersTab, etc.
  useEffect(() => {
    const handleZoomToMember = (event: Event) => {
      const customEvent = event as CustomEvent<ZoomToMemberEventDetail>;
      const detail = customEvent.detail;
      if (!detail?.userId) return;

      const targetUserId = Number(detail.userId);
      const memberUser = projectMembersMap[targetUserId];
      const memberLocation = projectMembersLocation.find((loc) => Number(loc.user?.id) === targetUserId);
      const displayName =
        detail.fullname ||
        memberUser?.fullname ||
        memberLocation?.user?.fullname ||
        detail.username ||
        memberUser?.username ||
        memberLocation?.user?.username ||
        `User ${targetUserId}`;
      const displayUsername =
        detail.username || memberUser?.username || memberLocation?.user?.username || '';

      const coords = parseMemberLocationCoords(memberLocation?.location);
      const map = window.map;

      if (coords && map) {
        map.flyTo({
          center: [coords.lng, coords.lat],
          zoom: 16,
          essential: true,
          duration: 1000,
        });

        if (activePopupRef.current) {
          try {
            activePopupRef.current.remove();
          } catch {
            // ignore
          }
        }

        const popup = new maplibregl.Popup({
          closeButton: true,
          closeOnClick: true,
        })
          .setLngLat([coords.lng, coords.lat])
          .setHTML(`
            <div style="padding: 8px;">
              <strong style="font-size: 14px;">${displayName}</strong>
              ${
                displayUsername
                  ? `<div style="font-size: 12px; color: #64748b; margin-top: 4px;">@${displayUsername}</div>`
                  : ''
              }
              ${
                coords.accuracy
                  ? `<div style="font-size: 11px; color: #94a3b8; margin-top: 8px;">Độ chính xác: ${coords.accuracy.toFixed(1)}m</div>`
                  : ''
              }
              ${
                memberLocation?.updatedAt
                  ? `<div style="font-size: 11px; color: #94a3b8; margin-top: 8px;">Cập nhật: ${formatDateTime(memberLocation.updatedAt)}</div>`
                  : ''
              }
            </div>
          `)
          .addTo(map);

        activePopupRef.current = popup;
      } else {
        message.info(`Chưa có vị trí trực tuyến của ${displayName} trên bản đồ`);
      }
    };

    window.addEventListener(ZOOM_TO_MEMBER_EVENT, handleZoomToMember);
    return () => {
      window.removeEventListener(ZOOM_TO_MEMBER_EVENT, handleZoomToMember);
    };
  }, [projectMembersLocation, projectMembersMap]);

  // Setup click handler for member markers
  useEffect(() => {
    if (!window.map || !isMapLoadedDone) return;

    const handleMemberClick = (e: maplibregl.MapMouseEvent) => {
      if (!e.features || e.features.length === 0) return;

      const feature = e.features[0];
      const userId = feature.properties?.userId;

      if (userId) {
        console.log('Member marker clicked, opening profile for userId:', userId);
        setUserProfileModal({
          visible: true,
          userId: userId
        });
      }
    };

    const handleCursorChange = () => {
      if (window.map) {
        window.map.getCanvas().style.cursor = 'pointer';
      }
    };

    const handleCursorReset = () => {
      if (window.map) {
        window.map.getCanvas().style.cursor = '';
      }
    };

    // Add click handler to member layer
    window.map.on('click', MemberMapUtilOptimized._layerId, handleMemberClick);
    window.map.on('mouseenter', MemberMapUtilOptimized._layerId, handleCursorChange);
    window.map.on('mouseleave', MemberMapUtilOptimized._layerId, handleCursorReset);

    return () => {
      if (window.map) {
        window.map.off('click', MemberMapUtilOptimized._layerId, handleMemberClick);
        window.map.off('mouseenter', MemberMapUtilOptimized._layerId, handleCursorChange);
        window.map.off('mouseleave', MemberMapUtilOptimized._layerId, handleCursorReset);
      }
    };
  }, [isMapLoadedDone, setUserProfileModal]);

  return (<div style={{ display: 'none' }}></div>);
};

export default memo(MemberMapController);
